<template>
  <div class="health-chart-container">
    <div class="chart-header">
      <div class="title-section">
        <div class="title-bar" :style="{ backgroundColor: barColor }" />
        <span class="chart-title">{{ title }}</span>
      </div>
      <div v-if="chartType !== 'ring'" class="time-tabs">
        <span
          v-for="tab in tabs"
          :key="tab"
          :class="['tab-item', { active: activeTab === tab }]"
          @click="handleTabChange(tab)"
        >
          {{ tab }}
        </span>
      </div>
    </div>

    <!-- 日视图：展示当天详细数据（日历样式） -->
    <div v-if="viewType === '1' && dayData && chartType !== 'ring'" class="day-view-calendar">
      <!-- 左侧装订线和日期 -->
      <div class="calendar-left">
        <!-- 螺旋装订效果 -->
        <!-- + -->

        <!-- 日期信息 -->
        <div class="date-info">
          <div class="date-number">{{ todayDate }}</div>
          <div class="date-year-month">{{ todayYearMonth }}</div>
          <div class="date-weekday">
            <span class="weekday-tag">星</span>
            <span class="weekday-tag">期</span>
            <span class="weekday-tag">{{ todayWeekday }}</span>
          </div>
        </div>
      </div>

      <!-- 中间虚线分隔线 -->
      <div class="calendar-divider" />

      <!-- 右侧内容 -->
      <div class="calendar-right">
        <div class="calendar-title">{{ title }}</div>
        <div class="calendar-count">
          <span class="count-text">打卡</span>
          <span class="count-num">{{ dayData.count || 0 }}次</span>
        </div>
      </div>
    </div>

    <!-- 周/月视图：展示图表 -->
    <div v-show="showCharts || chartType === 'ring'" class="chart-content-wrapper">
      <div class="chart-content" :class="{ 'ring-chart': chartType === 'ring' }">
        <!-- 左侧统计信息（可选） -->
        <div v-if="showStats && chartType !== 'ring'" class="stats-section">
          <div class="stats-icon" :style="{ backgroundColor: iconBgColor }">
            <slot name="icon">
              <i :class="iconClass" :style="{ color: primaryColor }" />
            </slot>
          </div>
          <div class="stats-value" :style="{ color: primaryColor }">{{ statsValue }}</div>
          <div class="stats-label">{{ statsLabel }}</div>
        </div>

        <!-- 图表区域 -->
        <div ref="chartRef" class="chart-area" :style="{ width: chartWidth, height: chartHeight }" />
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'HealthChart',
  props: {
    // 是否显示图表（控制周/月视图的显示）
    showCharts: {
      type: Boolean,
      default: true
    },
    // 视图类型：1-日视图，2-周视图，3-月视图
    viewType: {
      type: String,
      default: '3'
    },
    // 日视图数据（当viewType=1时使用）
    dayData: {
      type: Object,
      default: () => null
    },
    // 图表标题
    title: {
      type: String,
      default: '记录'
    },
    // 图表类型：bar-柱状图, line-面积图, ring-环形图
    chartType: {
      type: String,
      default: 'bar',
      validator: (value) => ['bar', 'line', 'ring'].includes(value)
    },
    // 主色调（用于渐变起始）
    primaryColor: {
      type: String,
      default: '#FF9F43'
    },
    // 次色调（用于渐变结束）
    secondaryColor: {
      type: String,
      default: '#FFD89B'
    },
    // 标题条颜色
    barColor: {
      type: String,
      default: '#333'
    },
    // 图标背景色
    iconBgColor: {
      type: String,
      default: '#FFF5E6'
    },
    // 图表数据
    chartData: {
      type: Array,
      default: () => []
    },
    // X轴数据
    xAxisData: {
      type: Array,
      default: () => ['12.15', '12.16', '12.17', '12.18', '12.19', '12.20', '12.21', '12.22', '12.23']
    },
    // 是否显示左侧统计
    showStats: {
      type: Boolean,
      default: false
    },
    // 统计数值
    statsValue: {
      type: [String, Number],
      default: '0'
    },
    // 统计标签
    statsLabel: {
      type: String,
      default: ''
    },
    // 图标类名
    iconClass: {
      type: String,
      default: 'el-icon-s-data'
    },
    // 图表宽度
    chartWidth: {
      type: String,
      default: '100%'
    },
    // 图表高度
    chartHeight: {
      type: String,
      default: '200px'
    },
    // Y轴最大值
    yAxisMax: {
      type: Number,
      default: null
    },
    // 是否显示Y轴
    showYAxis: {
      type: Boolean,
      default: true
    },
    // 是否显示数值标签
    showLabel: {
      type: Boolean,
      default: true
    },
    // 数值标签位置
    labelPosition: {
      type: String,
      default: 'top'
    },
    // 柱状图圆角
    barBorderRadius: {
      type: Array,
      default: () => [20, 20, 0, 0]
    },
    // 柱状图宽度
    barWidth: {
      type: [String, Number],
      default: '30%'
    },
    // 面积图是否显示平滑曲线
    smooth: {
      type: Boolean,
      default: true
    },
    // 面积图填充透明度
    areaOpacity: {
      type: Number,
      default: 0.3
    },
    // 环形图最大值（默认为100分）
    ringMax: {
      type: Number,
      default: 100
    }
  },
  data() {
    return {
      chartInstance: null,
      tabs: ['日', '周', '月'],
      activeTab: '月'
    }
  },
  computed: {
    todayFormatted() {
      const today = new Date()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      const weekDays = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
      const weekDay = weekDays[today.getDay()]
      return `${month}月${day}日 ${weekDay}`
    },
    todayDate() {
      return String(new Date().getDate()).padStart(2, '0')
    },
    todayYearMonth() {
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      return `${year}-${month}`
    },
    todayWeekday() {
      const weekDays = ['日', '一', '二', '三', '四', '五', '六']
      return weekDays[new Date().getDay()]
    }
  },
  watch: {
    chartData: {
      handler() {
        if ((this.viewType !== '1' && this.showCharts) || this.chartType === 'ring') {
          this.updateChart()
        }
      },
      deep: true
    },
    xAxisData: {
      handler() {
        if (this.viewType !== '1' && this.showCharts) {
          this.updateChart()
        }
      },
      deep: true
    },
    primaryColor() {
      if ((this.viewType !== '1' && this.showCharts) || this.chartType === 'ring') {
        this.updateChart()
      }
    },
    secondaryColor() {
      if ((this.viewType !== '1' && this.showCharts) || this.chartType === 'ring') {
        this.updateChart()
      }
    },
    viewType(newVal) {
      // 当视图类型切换时，更新activeTab
      const tabMap = { '1': '日', '2': '周', '3': '月' }
      this.activeTab = tabMap[newVal] || '月'

      // 切换到日视图时销毁图表实例（环形图除外）
      if (newVal === '1' && this.chartType !== 'ring') {
        this.disposeChart()
      } else if (this.showCharts || this.chartType === 'ring') {
        // 切换到周/月视图且需要显示图表时初始化
        this.$nextTick(() => {
          this.initChart()
        })
      }
    },
    showCharts(newVal) {
      // 监听showCharts变化，控制图表显示/隐藏（环形图除外）
      if (newVal && this.viewType !== '1') {
        this.$nextTick(() => {
          this.initChart()
        })
      } else if (this.chartType !== 'ring') {
        this.disposeChart()
      }
    }
  },
  mounted() {
    // 初始化activeTab
    const tabMap = { '1': '日', '2': '周', '3': '月' }
    this.activeTab = tabMap[this.viewType] || '月'

    if ((this.viewType !== '1' && this.showCharts) || this.chartType === 'ring') {
      this.initChart()
      window.addEventListener('resize', this.handleResize)
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.disposeChart()
  },
  methods: {
    initChart() {
      if (this.viewType === '1' && this.chartType !== 'ring' && !this.showCharts) return

      // 如果已有实例，先销毁
      if (this.chartInstance) {
        this.chartInstance.dispose()
      }

      this.$nextTick(() => {
        if (!this.$refs.chartRef) return

        this.chartInstance = echarts.init(this.$refs.chartRef)
        this.updateChart()

        // 添加resize监听
        window.addEventListener('resize', this.handleResize)
      })
    },
    disposeChart() {
      if (this.chartInstance) {
        this.chartInstance.dispose()
        this.chartInstance = null
      }
    },
    updateChart() {
      if (!this.chartInstance) return
      if (this.viewType === '1' && this.chartType !== 'ring' && !this.showCharts) return

      let option = {}

      if (this.chartType === 'ring') {
        option = this.getRingOption()
      } else if (this.chartType === 'bar') {
        option = this.getBarOption()
      } else if (this.chartType === 'line') {
        option = this.getLineOption()
      }

      this.chartInstance.setOption(option, true)
    },
    getRingOption() {
      // 获取分数值，默认为0
      const score = this.chartData[0] || 0
      const remaining = this.ringMax - score

      return {
        animation: true,
        animationDuration: 1500,
        animationEasing: 'cubicOut',
        series: [
          {
            type: 'pie',
            radius: ['60%', '75%'],
            center: ['50%', '50%'],
            startAngle: 90,
            data: [
              {
                value: score,
                name: '得分',
                itemStyle: {
                  color: this.primaryColor,
                  borderRadius: score === this.ringMax ? 0 : [0, 20, 20, 0]
                },
                label: { show: false },
                labelLine: { show: false },
                emphasis: {
                  scale: false,
                  itemStyle: {
                    color: this.primaryColor
                  }
                }
              },
              {
                value: remaining,
                name: '未得分',
                itemStyle: {
                  color: '#F0F0F0'
                },
                label: { show: false },
                labelLine: { show: false },
                emphasis: {
                  scale: false,
                  itemStyle: {
                    color: '#F0F0F0'
                  }
                },
                cursor: 'default'
              }
            ],
            silent: false,
            animationType: 'scale',
            animationTypeUpdate: 'scale'
          }
        ],
        graphic: [
          {
            type: 'text',
            left: 'center',
            top: '40%',
            style: {
              text: String(Math.round(score)),
              textAlign: 'center',
              fill: this.primaryColor,
              fontSize: 48,
              fontWeight: 'bold',
              fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif'
            }
          },
          {
            type: 'text',
            left: 'center',
            top: '58%',
            style: {
              text: '分',
              textAlign: 'center',
              fill: '#999',
              fontSize: 14,
              fontFamily: 'PingFang SC, Microsoft YaHei, sans-serif'
            }
          }
        ]
      }
    },
    getBarOption() {
      return {
        grid: {
          left: this.showStats ? '3%' : '8%',
          right: '4%',
          bottom: '10%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.xAxisData,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#999',
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          show: this.showYAxis,
          max: this.yAxisMax,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: '#F0F0F0',
              type: 'dashed'
            }
          },
          axisLabel: {
            color: '#999',
            fontSize: 12
          }
        },
        series: [{
          type: 'bar',
          data: this.chartData,
          barWidth: this.barWidth,
          itemStyle: {
            borderRadius: this.barBorderRadius,
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: this.primaryColor },
              { offset: 1, color: this.secondaryColor }
            ])
          },
          label: {
            show: this.showLabel,
            position: this.labelPosition,
            color: this.primaryColor,
            fontSize: 12,
            fontWeight: 'bold',
            formatter: '{c}'
          },
          emphasis: {
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: this.primaryColor },
                { offset: 1, color: this.secondaryColor }
              ])
            }
          }
        }]
      }
    },
    getLineOption() {
      return {
        grid: {
          left: this.showStats ? '3%' : '8%',
          right: '4%',
          bottom: '10%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.xAxisData,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            color: '#999',
            fontSize: 12
          }
        },
        yAxis: {
          type: 'value',
          show: this.showYAxis,
          max: this.yAxisMax,
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: '#F0F0F0',
              type: 'dashed'
            }
          },
          axisLabel: {
            color: '#999',
            fontSize: 12
          }
        },
        series: [{
          type: 'line',
          data: this.chartData,
          smooth: this.smooth,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            color: this.primaryColor,
            width: 2
          },
          itemStyle: {
            color: this.primaryColor,
            borderColor: '#fff',
            borderWidth: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: this.hexToRgba(this.primaryColor, this.areaOpacity) },
              { offset: 1, color: this.hexToRgba(this.primaryColor, 0) }
            ])
          },
          label: {
            show: this.showLabel,
            position: this.labelPosition,
            color: this.primaryColor,
            fontSize: 12,
            fontWeight: 'bold',
            formatter: '{c}'
          }
        }]
      }
    },
    // 辅助方法：hex颜色转rgba
    hexToRgba(hex, alpha) {
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    },
    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    },
    handleTabChange(tab) {
      this.activeTab = tab
      this.$emit('tab-change', tab)
    }
  }
}
</script>

<style scoped>
.health-chart-container {
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-bar {
  width: 4px;
  height: 16px;
  border-radius: 2px;
  margin-right: 10px;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.time-tabs {
  display: flex;
  gap: 8px;
}

.tab-item {
  padding: 4px 12px;
  font-size: 13px;
  color: #999;
  cursor: pointer;
  border-radius: 12px;
  transition: all 0.3s;
}

.tab-item.active {
  background: #4A90E2;
  color: #fff;
}

.tab-item:hover:not(.active) {
  color: #4A90E2;
}

.chart-content-wrapper {
  width: 100%;
}

.chart-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.chart-content.ring-chart {
  justify-content: center;
}

.stats-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
  padding: 12px;
}

.stats-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  font-size: 24px;
}

.stats-value {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 4px;
}

.stats-label {
  font-size: 12px;
  color: #999;
}

.chart-area {
  flex: 1;
}

/* 日历样式日视图 */
.day-view-calendar {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  margin-bottom: 16px;
}

.calendar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* 螺旋装订效果 */
.binding-rings {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
}

.ring {
  width: 14px;
  height: 14px;
  border: 2px solid #4A90E2;
  border-radius: 50%;
  position: relative;
  background: #fff;
}

.ring::before {
  content: '';
  position: absolute;
  top: 50%;
  left: -6px;
  width: 6px;
  height: 2px;
  background: #4A90E2;
  transform: translateY(-50%);
}

.ring::after {
  content: '';
  position: absolute;
  top: 50%;
  right: -6px;
  width: 6px;
  height: 2px;
  background: #4A90E2;
  transform: translateY(-50%);
}

/* 日期信息 */
.date-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.date-number {
  font-size: 48px;
  font-weight: 700;
  color: #FF6B6B;
  line-height: 1;
  margin-bottom: 4px;
}

.date-year-month {
  font-size: 13px;
  color: #FF6B6B;
  font-weight: 500;
  letter-spacing: 1px;
}

.date-weekday {
  display: flex;
  gap: 2px;
  margin-top: 6px;
}

.weekday-tag {
  background: #FF6B6B;
  color: #fff;
  font-size: 11px;
  padding: 2px 4px;
  border-radius: 3px;
  font-weight: 500;
  min-width: 18px;
  text-align: center;
}

/* 中间虚线分隔线 */
.calendar-divider {
  width: 1px;
  height: 80px;
  border-left: 1px dotted #ccc;
  margin: 0 24px;
}

/* 右侧内容 */
.calendar-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.calendar-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.calendar-count {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  color: #666;
}

.count-num {
  font-weight: 600;
  color: #333;
}

/* 记录列表容器 */
.day-records-container {
  margin-top: 16px;
}

.day-records-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
}

.record-card {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: #fafbfc;
  border-radius: 8px;
  border-left: 4px solid;
  transition: all 0.3s;
}

.record-card:hover {
  background: #f5f7fa;
  transform: translateX(4px);
}

.record-time {
  font-size: 13px;
  color: #999;
  min-width: 60px;
  margin-right: 16px;
}

.record-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.record-title {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.record-subtitle {
  font-size: 12px;
  color: #999;
}

.record-tags {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.record-tag {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
}

.record-value {
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.value-num {
  font-size: 20px;
  font-weight: 700;
}

.value-unit {
  font-size: 12px;
  color: #999;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #999;
}

.empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  font-size: 32px;
}

.empty-text {
  font-size: 14px;
}
</style>
